import React from 'react';

/**
 * Convert a decimal hours value into the format used by the OT
 * monitoring surfaces ("31h 22min" / "31h" / "0h").
 *
 * Kept colocated and intentionally NOT replaced with
 * ``utils/dateUtils.formatHoursMinutes`` — that shared helper renders
 * "31h 22m" / "—" (different abbreviation and null handling). The OT
 * surfaces have shipped with "min" since the dashboard launched and
 * changing the abbreviation now would create a visible regression on
 * both the admin /overtime-monitoring page and the upcoming
 * Compliance Watchlist. Centralisation of the formatter is a separate
 * cleanup, not part of this lift.
 */
const formatHoursMinutes = (decimalHours) => {
    if (!decimalHours || decimalHours === 0) return '0h';
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    if (minutes === 0) {
        return `${hours}h`;
    }
    return `${hours}h ${minutes}min`;
};

/**
 * Pick the threshold-driven colour class for an OT hours value.
 *
 * MOM Employment Act Part IV — 72h/month cap. Thresholds are:
 *   - 0–48h:   green  (compliant, comfortable headroom)
 *   - 48–60h:  yellow (at risk, formal email warning territory)
 *   - 60–72h:  orange (critical, manager intervention needed)
 *   - >72h:    red    (non-compliant, breach)
 *
 * Exported so the Compliance Watchlist card (Deliverable 1 Step 3)
 * can reuse the same colour for non-bar surfaces (KPI tile, list-row
 * accent stripe) and stay visually consistent with the admin
 * /overtime-monitoring page on the same worker.
 */
export const getOvertimeBarColorClass = (hours) => {
    if (hours > 72) return 'bg-red-500';
    if (hours > 60) return 'bg-orange-500';
    if (hours > 48) return 'bg-yellow-500';
    return 'bg-green-500';
};

/**
 * OvertimeProgressBar — visual progress indicator for MOM 72h OT hours.
 *
 * Lifted from
 * ``pages/OvertimeMonitoring/components/EmployeeOvertimeTable.js`` so
 * the upcoming team-dashboard Compliance Watchlist (Deliverable 1)
 * and the existing admin /overtime-monitoring table can render the
 * same indicator without code duplication. Stays byte-identical to
 * the original render when called with only the legacy
 * ``hours`` / ``max`` props.
 *
 * Props
 * -----
 * @param {number} hours
 *   Current cap-eligible OT hours. Drives the filled-bar width and
 *   the threshold-tinted colour class.
 * @param {number} [max=72]
 *   Denominator for the percentage. Defaults to the MOM monthly cap.
 *   Colour thresholds are 48/60/72 regardless of ``max`` — this prop
 *   is preserved for backward-compat but in practice ``max`` is
 *   always 72 on the OT surfaces.
 * @param {number} [projectedHours]
 *   Optional projected end-of-month OT hours from the two-factor
 *   forecast (``calculate_ot_forecast`` → ``projectedEomOt``). When
 *   provided AND strictly greater than ``hours``, the component
 *   renders a thin amber marker at the projected % position so the
 *   viewer can see how close the trajectory is to the cap WITHOUT
 *   moving the current-hours visual reference. When omitted or
 *   ``<= hours``, the bar renders identically to the original.
 * @param {boolean} [compact=false]
 *   Tighter footprint for mobile list rows (smaller bar height,
 *   smaller text, removed "of 72h" suffix). Forward-compat for the
 *   Watchlist's mobile-first row layout. Desktop admin table keeps
 *   the spacious default.
 * @param {string} [ariaLabel]
 *   Optional screen-reader label. Defaults to a descriptive
 *   "X of Y hours" string.
 */
export const OvertimeProgressBar = ({
    hours,
    max = 72,
    projectedHours,
    compact = false,
    ariaLabel,
}) => {
    const safeHours = Number.isFinite(hours) ? hours : 0;
    const percentage = Math.min(100, (safeHours / max) * 100);
    const colorClass = getOvertimeBarColorClass(safeHours);

    const hasProjected =
        Number.isFinite(projectedHours) && projectedHours > safeHours;
    const projectedPercentage = hasProjected
        ? Math.min(100, (projectedHours / max) * 100)
        : null;

    const wrapperClass = compact
        ? 'space-y-0.5 min-w-[88px]'
        : 'space-y-1 min-w-[120px]';
    const textRowClass = compact
        ? 'flex justify-between text-[11px] leading-tight'
        : 'flex justify-between text-xs';
    const barHeightClass = compact ? 'h-1.5' : 'h-2';

    const label =
        ariaLabel ||
        `${formatHoursMinutes(safeHours)} of ${max}h overtime${
            hasProjected
                ? `, projected ${formatHoursMinutes(projectedHours)} by month end`
                : ''
        }`;

    return (
        <div
            className={wrapperClass}
            role="progressbar"
            aria-valuenow={Math.round(safeHours * 10) / 10}
            aria-valuemin={0}
            aria-valuemax={max}
            aria-label={label}
        >
            <div className={textRowClass}>
                <span className="font-medium text-gray-700">
                    {formatHoursMinutes(safeHours)}
                </span>
                {!compact && (
                    <span className="text-gray-500">of {max}h</span>
                )}
            </div>
            <div
                className={`relative w-full bg-gray-200 rounded-full ${barHeightClass}`}
            >
                <div
                    className={`${barHeightClass} rounded-full transition-all duration-300 ${colorClass}`}
                    style={{ width: `${percentage}%` }}
                />
                {hasProjected && (
                    // Projected EOM marker — thin amber tick. Rendered
                    // ON TOP of the filled bar so it never visually
                    // shifts the user's read of the current-hours bar.
                    // Hidden behind aria-hidden because the descriptive
                    // label on the wrapper already covers it for SRs.
                    <div
                        aria-hidden="true"
                        className="absolute top-0 bottom-0 w-0.5 bg-amber-600 rounded-full"
                        style={{ left: `${projectedPercentage}%` }}
                        title={`Projected ${formatHoursMinutes(projectedHours)} by month end`}
                    />
                )}
            </div>
        </div>
    );
};

export default OvertimeProgressBar;
